import { assertCompleteRows } from "./aggregateState";
// ==========================================================================
// lib/vehicles.ts — vehicle, trip, odometer and logbook data (FF-008)
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId, useMe } from "./household";

export type VehicleType = "car" | "ute" | "van" | "motorbike" | "boat" | "jetski" | "trailer" | "caravan" | "other";
/** Road vehicles that carry people get trips and the logbook; the rest are value, rego and costs (032). */
export const VEHICLE_TYPES: { key: VehicleType; label: string; emoji: string; trips: boolean }[] = [
  { key: "car", label: "Car", emoji: "🚙", trips: true }, { key: "ute", label: "Ute", emoji: "🛻", trips: true }, { key: "van", label: "Van", emoji: "🚐", trips: true },
  { key: "motorbike", label: "Motorbike", emoji: "🏍️", trips: true }, { key: "boat", label: "Boat", emoji: "🚤", trips: false }, { key: "jetski", label: "Jet ski", emoji: "🌊", trips: false },
  { key: "trailer", label: "Trailer", emoji: "🛞", trips: false }, { key: "caravan", label: "Caravan", emoji: "🏕️", trips: false }, { key: "other", label: "Other", emoji: "🔧", trips: false },
];
export const typeOf = (t: VehicleType | undefined) => VEHICLE_TYPES.find((x) => x.key === (t ?? "car")) ?? VEHICLE_TYPES[0];
export type SoldHow = "private" | "dealer" | "trade_in" | "auction" | "marketplace" | "written_off" | "gifted" | "other";
export const SOLD_HOW: { key: SoldHow; label: string }[] = [
  { key: "private", label: "Private sale" }, { key: "marketplace", label: "Marketplace / online" }, { key: "dealer", label: "To a dealer" }, { key: "trade_in", label: "Traded in" },
  { key: "auction", label: "Auction" }, { key: "written_off", label: "Written off" }, { key: "gifted", label: "Given away" }, { key: "other", label: "Other" },
];
export type Proceeds = "bank" | "cash" | "trade_in" | "none";
export type Sale = {
  sold_price_cents: number | null; sold_how: SoldHow | null; sold_to: string | null; proceeds: Proceeds | null; proceeds_account_id: string | null;
  proceeds_expected_on: string | null; proceeds_transaction_id: string | null; traded_for_asset_id: string | null; sale_note: string | null;
};
export type Vehicle = {
  asset_id: string; make_model: string | null; rego: string | null; year: number | null; vehicle_type: VehicleType;
  // Value and finance (024): owned outright, a car loan, or a novated lease (salary sacrifice).
  finance: "owned" | "loan" | "novated"; value_cents: number | null; value_as_at: string | null; loan_account_id: string | null;
  lease_provider: string | null; lease_payment_cents: number | null; lease_frequency: "weekly" | "fortnightly" | "monthly" | null;
  lease_ends_on: string | null; residual_cents: number | null;
  assets: { name: string; acquired_on: string | null; disposed_on: string | null; notes: string | null; cost_cents: number | null } & Sale;
};
export type UsePeriod = { id: string; asset_id: string; use: "personal" | "work" | "rental" | "vacant"; valid_from: string; valid_to: string | null };
export type Trip = {
  id: string; vehicle_id: string; driver_person_id: string | null; trip_date: string; start_km: number; end_km: number; distance_km: number;
  from_place: string | null; to_place: string | null; purpose: string; job_ref: string | null; employer_entity_id: string | null;
  proposed_use: "work" | "private" | "unsure"; reviewed_use: "work" | "private" | null; logbook_period_id: string | null;
};
export type Odometer = { id: string; vehicle_id: string; read_on: string; km: number; kind: string; note: string | null };
export type Logbook = { id: string; vehicle_id: string; starts_on: string; ends_on: string; opening_km: number | null; closing_km: number | null; status: "running" | "complete" | "reviewed" };
export type TaxRate = { financial_year_id: string; key: string; value: number | null; unit: string };

/** Australian financial year for a date, e.g. "2026-27". */
export function fyOf(d: Date | string): string {
  const x = typeof d === "string" ? new Date(d) : d;
  const y = x.getFullYear(); const m = x.getMonth() + 1;
  const start = m >= 7 ? y : y - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
}
export function fyRange(fy: string): { from: string; to: string } {
  const y = Number(fy.slice(0, 4));
  return { from: `${y}-07-01`, to: `${y + 1}-06-30` };
}
export const todayIso = () => new Date().toISOString().slice(0, 10);

const inv = (qc: ReturnType<typeof useQueryClient>, hid: string | undefined) => () => {
  for (const k of ["vehicles", "use_periods", "trips", "odometer", "logbooks", "audit"]) void qc.invalidateQueries({ queryKey: [k, hid] });
};

export function useVehicles() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["vehicles", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error, count } = await supabase.from("vehicles").select("asset_id,make_model,rego,year,vehicle_type,finance,value_cents,value_as_at,loan_account_id,lease_provider,lease_payment_cents,lease_frequency,lease_ends_on,residual_cents,assets!inner(name,acquired_on,disposed_on,notes,cost_cents,deleted_at,sold_price_cents,sold_how,sold_to,proceeds,proceeds_account_id,proceeds_expected_on,proceeds_transaction_id,traded_for_asset_id,sale_note)", { count: "exact" }).eq("household_id", hid).is("assets.deleted_at", null);
      if (error) throw error;
      assertCompleteRows({ data, count }, "vehicles");
      const n = (v: number | null) => (v === null ? null : Number(v));
      return (data as unknown as Vehicle[]).map((v) => ({ ...v, value_cents: n(v.value_cents), lease_payment_cents: n(v.lease_payment_cents), residual_cents: n(v.residual_cents), assets: { ...v.assets, cost_cents: n(v.assets.cost_cents) } }))
        .sort((a, b) => a.assets.name.localeCompare(b.assets.name));
    },
  });
}

export function useUsePeriods() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["use_periods", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("asset_use_periods").select("id,asset_id,use,valid_from,valid_to").eq("household_id", hid).is("deleted_at", null).order("valid_from");
      if (error) throw error;
      return data as UsePeriod[];
    },
  });
}

export function useTrips(vehicleId?: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["trips", hid, vehicleId ?? "all"], enabled: !!hid,
    queryFn: async () => {
      let q = supabase.from("vehicle_trips").select("*").eq("household_id", hid).is("deleted_at", null).order("trip_date", { ascending: false }).order("start_km", { ascending: false });
      if (vehicleId) q = q.eq("vehicle_id", vehicleId);
      const { data, error } = await q;
      if (error) throw error;
      return data as Trip[];
    },
  });
}

export function useOdometer(vehicleId?: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["odometer", hid, vehicleId ?? "all"], enabled: !!hid,
    queryFn: async () => {
      let q = supabase.from("odometer_readings").select("*").eq("household_id", hid).is("deleted_at", null).order("read_on", { ascending: false });
      if (vehicleId) q = q.eq("vehicle_id", vehicleId);
      const { data, error } = await q;
      if (error) throw error;
      return data as Odometer[];
    },
  });
}

export function useLogbooks(vehicleId?: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["logbooks", hid, vehicleId ?? "all"], enabled: !!hid,
    queryFn: async () => {
      let q = supabase.from("logbook_periods").select("*").eq("household_id", hid).is("deleted_at", null).order("starts_on", { ascending: false });
      if (vehicleId) q = q.eq("vehicle_id", vehicleId);
      const { data, error } = await q;
      if (error) throw error;
      return data as Logbook[];
    },
  });
}

export function useTaxRates() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["tax_rates", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("tax_rates").select("financial_year_id,key,value,unit");
      if (error) throw error;
      return data as TaxRate[];
    },
  });
}

export type TripDraft = {
  vehicle_id: string; trip_date: string; start_km: number; end_km: number; purpose: string;
  from_place?: string | null; to_place?: string | null; job_ref?: string | null; employer_entity_id?: string | null;
  proposed_use: "work" | "private" | "unsure";
};

export function useSaveTrip() {
  const hid = useHouseholdId(); const me = useMe(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, draft }: { id?: string; draft: TripDraft }) => {
      const row = { ...draft, household_id: hid, driver_person_id: me?.id ?? null };
      const { error } = id ? await supabase.from("vehicle_trips").update(row).eq("id", id) : await supabase.from("vehicle_trips").insert(row);
      if (error) throw error;
    },
    onSuccess: inv(qc, hid),
  });
}

export function useReviewTrip() {
  const hid = useHouseholdId(); const me = useMe(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, use }: { id: string; use: "work" | "private" | null }) => {
      const { error } = await supabase.from("vehicle_trips").update({ reviewed_use: use, reviewed_by: use ? me?.id ?? null : null }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: inv(qc, hid),
  });
}

export function useSaveVehicle() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { asset_id?: string; name: string; vehicle_type?: VehicleType; make_model: string | null; rego: string | null; year: number | null; acquired_on: string | null; owner_entity_id?: string; work_from: string | null;
      cost_cents?: number | null; money?: Pick<Vehicle, "finance" | "value_cents" | "value_as_at" | "loan_account_id" | "lease_provider" | "lease_payment_cents" | "lease_frequency" | "lease_ends_on" | "residual_cents"> }) => {
      let assetId = v.asset_id;
      if (!assetId) {
        const { data, error } = await supabase.from("assets").insert({ household_id: hid, kind: "vehicle", name: v.name, acquired_on: v.acquired_on, cost_cents: v.cost_cents ?? null }).select("id").single();
        if (error) throw error;
        assetId = (data as { id: string }).id;
        const { error: e2 } = await supabase.from("vehicles").insert({ asset_id: assetId, household_id: hid, vehicle_type: v.vehicle_type ?? "car", make_model: v.make_model, rego: v.rego, year: v.year, ...(v.money ?? {}) });
        if (e2) throw e2;
        if (v.owner_entity_id) {
          const { error: e3 } = await supabase.from("asset_ownerships").insert({ household_id: hid, asset_id: assetId, owner_entity_id: v.owner_entity_id, share_pct: 100 });
          if (e3) throw e3;
        }
      } else {
        const { error } = await supabase.from("assets").update({ name: v.name, acquired_on: v.acquired_on, ...(v.cost_cents !== undefined ? { cost_cents: v.cost_cents } : {}) }).eq("id", assetId);
        if (error) throw error;
        const { error: e2 } = await supabase.from("vehicles").update({ ...(v.vehicle_type ? { vehicle_type: v.vehicle_type } : {}), make_model: v.make_model, rego: v.rego, year: v.year, ...(v.money ?? {}) }).eq("asset_id", assetId);
        if (e2) throw e2;
      }
      // Work use: one open period from the given date; clearing the date closes it.
      const { data: open } = await supabase.from("asset_use_periods").select("id,valid_from").eq("asset_id", assetId).eq("use", "work").is("valid_to", null).is("deleted_at", null).maybeSingle();
      if (v.work_from && !open) {
        const { error } = await supabase.from("asset_use_periods").insert({ household_id: hid, asset_id: assetId, use: "work", valid_from: v.work_from });
        if (error) throw error;
      } else if (v.work_from && open && open.valid_from !== v.work_from) {
        const { error } = await supabase.from("asset_use_periods").update({ valid_from: v.work_from }).eq("id", open.id);
        if (error) throw error;
      } else if (!v.work_from && open) {
        const { error } = await supabase.from("asset_use_periods").update({ valid_to: todayIso() }).eq("id", open.id);
        if (error) throw error;
      }
      return assetId;
    },
    onSuccess: inv(qc, hid),
  });
}

export function useSaveOdometer() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (o: { vehicle_id: string; read_on: string; km: number; kind: string; note?: string | null }) => {
      const { error } = await supabase.from("odometer_readings").insert({ ...o, household_id: hid });
      if (error) throw error;
    },
    onSuccess: inv(qc, hid),
  });
}

export function useSaveLogbook() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (l: { id?: string; vehicle_id: string; starts_on: string; ends_on: string; opening_km: number | null; closing_km?: number | null; status?: Logbook["status"] }) => {
      const row = { ...l, household_id: hid };
      const { error } = l.id ? await supabase.from("logbook_periods").update(row).eq("id", l.id) : await supabase.from("logbook_periods").insert(row);
      if (error) throw error;
    },
    onSuccess: inv(qc, hid),
  });
}

/** Figures for a vehicle card. */
export function vehicleStats(vehicleId: string, trips: Trip[], periods: UsePeriod[], fy = fyOf(new Date())) {
  const r = fyRange(fy);
  const mine = trips.filter((t) => t.vehicle_id === vehicleId);
  const inFy = mine.filter((t) => t.trip_date >= r.from && t.trip_date <= r.to);
  const useOf = (t: Trip) => t.reviewed_use ?? (t.proposed_use === "unsure" ? null : t.proposed_use);
  const workKm = inFy.filter((t) => useOf(t) === "work").reduce((s, t) => s + t.distance_km, 0);
  const privateKm = inFy.filter((t) => useOf(t) === "private").reduce((s, t) => s + t.distance_km, 0);
  const unsure = inFy.filter((t) => useOf(t) === null).length;
  const toReview = inFy.filter((t) => !t.reviewed_use).length;
  const lastKm = mine.length ? Math.max(...mine.map((t) => t.end_km)) : null;
  const work = periods.find((p) => p.asset_id === vehicleId && p.use === "work" && !p.valid_to);
  return { fy, trips: inFy.length, workKm, privateKm, totalKm: workKm + privateKm, unsure, toReview, lastKm, workFrom: work?.valid_from ?? null, lastTrip: mine[0] ?? null };
}

/**
 * Best 12-week (84-day) window by work share, from continuous trip logging.
 * Only windows fully covered by logged trips count (start + 83 days must not
 * pass the last logged trip). Until 12 weeks exist, returns the running
 * figure over everything logged, flagged enoughData: false.
 */
export function bestLogbookWindow(trips: Trip[]) {
  const useOf = (t: Trip) => t.reviewed_use ?? (t.proposed_use === "unsure" ? null : t.proposed_use);
  const dated = trips.filter((t) => useOf(t) !== null).sort((a, b) => a.trip_date.localeCompare(b.trip_date));
  if (dated.length === 0) return null;
  const day = 864e5;
  const last = dated[dated.length - 1].trip_date;
  const sum = (list: Trip[]) => {
    const totalKm = list.reduce((s, t) => s + t.distance_km, 0);
    const workKm = list.filter((t) => useOf(t) === "work").reduce((s, t) => s + t.distance_km, 0);
    return { totalKm, workKm, pct: totalKm ? (workKm / totalKm) * 100 : 0, trips: list.length };
  };
  let best: { from: string; to: string; workKm: number; totalKm: number; pct: number; trips: number } | null = null;
  for (const start of dated) {
    const toIso = new Date(new Date(start.trip_date + "T00:00Z").getTime() + 83 * day).toISOString().slice(0, 10);
    if (toIso > last) break;
    const r = sum(dated.filter((t) => t.trip_date >= start.trip_date && t.trip_date <= toIso));
    if (r.totalKm && (!best || r.pct > best.pct)) best = { from: start.trip_date, to: toIso, ...r };
  }
  if (best) return { ...best, enoughData: true };
  return { from: dated[0].trip_date, to: last, ...sum(dated), enoughData: false };
}

/** Mark an asset sold (or undo it). Work use ends on the sale date (032). */
export function useSellAsset() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { assetId: string; disposed_on: string | null } & Partial<Sale>) => {
      const { assetId, ...row } = a;
      const clear = { sold_price_cents: null, sold_how: null, sold_to: null, proceeds: null, proceeds_account_id: null, proceeds_expected_on: null, proceeds_transaction_id: null, traded_for_asset_id: null, sale_note: null };
      const { error } = await supabase.from("assets").update(row.disposed_on ? { ...clear, ...row } : { ...clear, disposed_on: null }).eq("id", assetId);
      if (error) throw error;
      if (row.disposed_on) {
        const { error: e2 } = await supabase.from("asset_use_periods").update({ valid_to: row.disposed_on }).eq("asset_id", assetId).is("valid_to", null).is("deleted_at", null);
        if (e2) throw e2;
      }
    },
    onSuccess: () => { inv(qc, hid)(); for (const k of ["unreconciled", "sales"]) void qc.invalidateQueries({ queryKey: [k, hid] }); },
  });
}

/** Sales still waiting for their money to land in the bank (for Reconcile). */
export type ExpectedSale = { id: string; name: string; disposed_on: string; sold_price_cents: number; proceeds_account_id: string; proceeds_expected_on: string | null };
export function useExpectedSales() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["sales", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("assets").select("id,name,disposed_on,sold_price_cents,proceeds_account_id,proceeds_expected_on")
        .eq("household_id", hid).eq("proceeds", "bank").is("proceeds_transaction_id", null).not("disposed_on", "is", null).is("deleted_at", null);
      if (error) throw error;
      return (data as ExpectedSale[]).filter((x) => x.sold_price_cents > 0 && x.proceeds_account_id);
    },
  });
}
